/* API service layer for configuration operations */

import axios, { AxiosResponse } from 'axios'
import { API_BASE_URL } from '../constants/system'
import {
  Config,
  ConfigData,
  ConfigType,
  ConfigReference,
  ListConfigsQuery,
  MergedConfigQuery,
  MergedConfigResult
} from '../types/config'
import { ConfigSchema } from '../types/gotypes'

// Base URL for config API endpoints
const CONFIG_API_BASE = `${API_BASE_URL}/configs`

// API response types
interface ListConfigsResponse {
  configs: Config[]
  count: number
  query: ListConfigsQuery
}

interface MergedConfigResponse {
  result: MergedConfigResult
  query: MergedConfigQuery
}

// Response type for bulk import
export interface BulkImportResult {
  updated: number
  created: number
  skipped: number
  errors?: string[]
}

// Main config API service
export const configApi = {
  /**
   * Get config field definitions schema for the UI
   */
  async getSchema(): Promise<ConfigSchema> {
    const response: AxiosResponse<ConfigSchema> = await axios.get(
      `${CONFIG_API_BASE}/schema`
    )
    return response.data
  },

  /**
   * Get merged configuration for an entity with audit trail
   */
  async getMergedConfig(customer: string, job: string, query: MergedConfigQuery): Promise<MergedConfigResult> {
    const params = new URLSearchParams({
      config_type: query.config_type.toString(),
      customer: query.customer,
    })

    if (query.job) params.append('job', query.job)
    if (query.folder_path) params.append('folder_path', query.folder_path)
    if (query.table_name) params.append('table_name', query.table_name)

    const response: AxiosResponse<MergedConfigResponse> = await axios.get(
      `${CONFIG_API_BASE}/${customer}/${job}/merged?${params.toString()}`
    )
    
    return response.data.result
  },

  /**
   * Get a specific configuration by ID
   */
  async getConfig(customer: string, job: string, id: string): Promise<Config> {
    const response: AxiosResponse<Config> = await axios.get(
      `${CONFIG_API_BASE}/${customer}/${job}/${id}`
    )
    return response.data
  },

  /**
   * Create a new configuration
   */
  async createConfig(customer: string, job: string, config: Omit<Config, 'id' | 'created_at' | 'updated_at'>): Promise<Config> {
    const response: AxiosResponse<Config> = await axios.post(
      `${CONFIG_API_BASE}/${customer}/${job}`,
      config
    )
    return response.data
  },

  /**
   * Update an existing configuration
   */
  async updateConfig(customer: string, job: string, id: string, config: Omit<Config, 'id' | 'created_at' | 'updated_at'>): Promise<Config> {
    const response: AxiosResponse<Config> = await axios.put(
      `${CONFIG_API_BASE}/${customer}/${job}/${id}`,
      { ...config, id }
    )
    return response.data
  },

  /**
   * Delete a configuration
   */
  async deleteConfig(customer: string, job: string, id: string): Promise<void> {
    await axios.delete(`${CONFIG_API_BASE}/${customer}/${job}/${id}`)
  },

  /**
   * List configurations with optional filtering
   */
  async listConfigs(customer: string, job: string, query?: ListConfigsQuery): Promise<Config[]> {
    const params = new URLSearchParams()
    
    if (query) {
      if (query.config_type !== undefined) params.append('config_type', query.config_type.toString())
      if (query.customer) params.append('customer', query.customer)
      if (query.job) params.append('job', query.job)
      if (query.folder_path) params.append('folder_path', query.folder_path)
      if (query.table_name) params.append('table_name', query.table_name)
    }

    const url = params.toString() 
      ? `${CONFIG_API_BASE}/${customer}/${job}?${params.toString()}`
      : `${CONFIG_API_BASE}/${customer}/${job}`

    const response: AxiosResponse<ListConfigsResponse> = await axios.get(url)
    return response.data.configs
  },

  /**
   * Get configurations that exist for entities in the TOC
   * This helps identify which nodes in the tree have custom configs
   */
  async getConfiguredEntities(customer: string, job: string): Promise<Set<string>> {
    const configs = await this.listConfigs(customer, job)
    const configuredEntities = new Set<string>()

    configs.forEach(config => {
      // Create a unique identifier for each configured entity
      let entityId = `${config.config_type}`

      switch (config.config_type) {
        case ConfigType.Customer:
          entityId += `:${config.customer}`
          break
        case ConfigType.Job:
          entityId += `:${config.customer}:${config.job}`
          break
        case ConfigType.Folder:
          entityId += `:${config.customer}:${config.job}:${config.folder_path}`
          break
        case ConfigType.Table:
          entityId += `:${config.customer}:${config.job}:${config.folder_path}:${config.table_name}`
          break
      }

      configuredEntities.add(entityId)
    })

    return configuredEntities
  },

  /**
   * Export bulk table configs as CSV
   * @param customerId Numerical customer ID (for licensing API)
   * @param customerName Customer name string (for Carbon API)
   * @param job Optional job name - if not provided, exports all jobs
   * @returns Blob containing CSV data
   */
  async exportBulkTableConfig(customerId: string, customerName: string, job?: string): Promise<Blob> {
    const params = new URLSearchParams()
    params.append('customer_name', customerName)
    if (job) params.append('job', job)

    const url = `${CONFIG_API_BASE}/${customerId}/bulk-table-export?${params.toString()}`

    const response = await axios.get(url, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Import bulk table configs from CSV
   * @param customer Customer name
   * @param file CSV file to import
   * @returns Import result with counts
   */
  async importBulkTableConfig(customer: string, file: File): Promise<BulkImportResult> {
    const formData = new FormData()
    formData.append('file', file)

    const response: AxiosResponse<BulkImportResult> = await axios.post(
      `${CONFIG_API_BASE}/${customer}/bulk-table-import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    return response.data
  }
}

// Utility functions for working with configurations

/**
 * Create a config entity identifier for tracking configured nodes
 */
export function createEntityId(type: ConfigType, customer: string, job?: string, folderPath?: string, tableName?: string): string {
  let entityId = `${type}`
  
  switch (type) {
    case ConfigType.Customer:
      entityId += `:${customer}`
      break
    case ConfigType.Job:
      entityId += `:${customer}:${job}`
      break
    case ConfigType.Folder:
      entityId += `:${customer}:${job}:${folderPath}`
      break
    case ConfigType.Table:
      entityId += `:${customer}:${job}:${folderPath}:${tableName}`
      break
  }

  return entityId
}

/**
 * Get the display name for a config type
 */
export function getConfigTypeDisplayName(type: ConfigType): string {
  switch (type) {
    case ConfigType.Global:
      return 'Global'
    case ConfigType.Customer:
      return 'Customer'
    case ConfigType.Job:
      return 'Job'
    case ConfigType.Folder:
      return 'Folder'
    case ConfigType.Table:
      return 'Table'
    default:
      return 'Unknown'
  }
}

/**
 * Create a user-friendly description of where a config value comes from
 */
export function getSourceDescription(
  reference: ConfigReference,
  displayNames?: { customerDisplayName?: string | null; jobDisplayName?: string | null }
): string {
  const source = reference.source_entity
  const type = getConfigTypeDisplayName(source.config_type || ConfigType.Global)

  switch (source.config_type) {
    case ConfigType.Global:
      return `Global default`
    case ConfigType.Customer:
      return `Customer: ${displayNames?.customerDisplayName || source.customer}`
    case ConfigType.Job:
      return `Job: ${displayNames?.jobDisplayName || source.job}`
    case ConfigType.Folder:
      return `Folder: ${source.folder_path}`
    case ConfigType.Table:
      return `Table: ${source.table_name}`
    default:
      return `${type} configuration`
  }
}